import CloseIcon from '@mui/icons-material/Close'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import OpenInFullIcon from '@mui/icons-material/OpenInFull'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Drawer from '@mui/material/Drawer'
import IconButton from '@mui/material/IconButton'
import LinearProgress from '@mui/material/LinearProgress'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'

import {
  useAddExceptionMediaMutation,
  useCreateWorkOrderExceptionMutation,
  useUpdateWorkOrderExceptionMutation,
} from 'api/work-order/workOrderException'
import CancelButton from 'components/buttons/CancelButton'
import HHConfirmDialog from 'components/common/HHConfirmDialog'
import HHDialogTitle from 'components/common/HHDialogTitle'
import { MAIN_HEADER_HEIGHT } from 'components/common/NavbarWrapper'
import { HHFormTextField } from 'components/form-fields/v5'
import { EXCEPTION_TYPE, usesDescriptionChannel } from 'settings/constants/exceptionType'
import T from 'T'
import { handleError } from 'utils/error'
import { getExceptionDelayMs, getExceptionDescriptionValue, getExceptionResponsibleParty } from 'utils/exceptionFields'
import { EXCEPTION_MEDIA_KIND, getExceptionMediaSlideKey } from 'utils/exceptionImage'
import { uploadExceptionMediaFile } from 'utils/exceptionMediaUpload'
import { get } from 'utils/lodash'

import ExceptionVideoSlide from '../exception-card/ExceptionVideoSlide'
import ExceptionCardBottomOverlay from '../ExceptionCardBottomOverlay'
import ExceptionCardMediaSwiperContainer from '../ExceptionCardMediaSwiperContainer'
import ExceptionImage from '../ExceptionImage'
import ExceptionMapImage from '../ExceptionMapImage'
import ImagePlaceholder from '../ImagePlaceholder'

import { EXCEPTION_FORM_FIELD, EXCEPTION_TARGET_WORK_ORDER } from './constants'
import ExceptionAuditTrail from './ExceptionAuditTrail'
import ExceptionMediaUploader from './ExceptionMediaUploader'
import ExceptionTargetSelect from './ExceptionTargetSelect'
import ExceptionTypeFields from './ExceptionTypeFields'
import ExceptionTypeSelect from './ExceptionTypeSelect'

import type { WorkOrderContainer } from '../../containers/types'
import type { ExceptionImageItem, ExceptionShape } from '../exception-card/types'
import type { ExceptionFormValues } from './constants'

const ExceptionImageUntyped = ExceptionImage as React.ElementType
const ImagePlaceholderUntyped = ImagePlaceholder as React.ElementType
const ExceptionCardMediaSwiperContainerUntyped = ExceptionCardMediaSwiperContainer as React.ElementType
const ExceptionCardBottomOverlayUntyped = ExceptionCardBottomOverlay as React.ElementType

const DRAWER_WIDTH = { xs: '100%', sm: 460 }

// Viewport-relative so the expanded map (title + map) fits below the fixed app header — mirrors
// WorkOrderLocationMap. Capped at 640 so it doesn't grow unwieldy on tall screens.
const EXPANDED_MAP_HEIGHT = 'min(640px, calc(100vh - 160px))'

const toStringValue = (value: unknown): string => (value == null ? '' : String(value))

const buildDefaults = (
  exception: ExceptionShape,
  note?: string | null,
  isCreate = false,
  initialContainerId?: string | null
): ExceptionFormValues => ({
  // New exceptions default to Camera (Photo) per design; an existing exception keeps its own type.
  exceptionType: get(exception, 'exceptionType', '') || (isCreate ? EXCEPTION_TYPE.PHOTO : ''),
  noteText: note ?? '',
  // Same flat→metadata.* fallbacks as the card pill (utils/exceptionFields), so
  // driver-app values prefill the drawer instead of rendering empty groups.
  description: getExceptionDescriptionValue(exception),
  latitude: toStringValue(get(exception, 'latitude')),
  longitude: toStringValue(get(exception, 'longitude')),
  responsibleParty: getExceptionResponsibleParty(exception),
  // delayTime (mobile) with the description fallback (web-created Delay exceptions).
  delayMs: String(getExceptionDelayMs(exception) || ''),
  // WO-level uses the sentinel (not '') so the select input shows "Work Order"; the
  // per-container warning icon pre-scopes a create to its container. The legacy mobile
  // `workOrderContainerId` is deliberately NOT a fallback here: this value feeds the PATCH's
  // `containerId`, which is inventory-container UUID space — sending a legacy-space id would
  // mislink the container (cards may still use it for display resolution, never for saves).
  workOrderContainerId:
    get(exception, 'container.id', '') ||
    get(exception, 'containerId', '') ||
    (isCreate ? initialContainerId ?? EXCEPTION_TARGET_WORK_ORDER : ''),
})

type ExceptionDetailModalProps = {
  open: boolean
  onClose: () => void
  mode?: 'edit' | 'create'
  accountId?: string
  workOrderId?: string
  exception: ExceptionShape
  date?: string
  time?: string
  images?: ExceptionImageItem[]
  note?: string | null
  containers?: WorkOrderContainer[]
  initialContainerId?: string | null
  isWorkOrderInvoiced?: boolean
  revisions?: unknown[]
  // Rendered beside the drawer title. Board V2 pages through the stop's exceptions with it.
  headerActions?: React.ReactNode
  // Opens the fullscreen gallery for this exception's media (wired by the card).
  onMediaClick?: () => void
}

const ExceptionDetailModal: React.FC<ExceptionDetailModalProps> = ({
  open,
  onClose,
  mode = 'edit',
  accountId,
  workOrderId,
  exception,
  date,
  time,
  images = [],
  note,
  containers = [],
  initialContainerId = null,
  isWorkOrderInvoiced = false,
  revisions,
  headerActions,
  onMediaClick,
}) => {
  const id = get(exception, 'id')
  const isCreate = mode === 'create'
  const hasImages = Array.isArray(images) && images.length > 0
  const [media, setMedia] = useState<File[]>([])
  const [confirmDiscardOpen, setConfirmDiscardOpen] = useState(false)
  const [isMapExpanded, setIsMapExpanded] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const isSubmittingRef = useRef(false)

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { isDirty, dirtyFields },
  } = useForm<ExceptionFormValues>({ defaultValues: buildDefaults(exception, note, isCreate, initialContainerId) })

  const [updateWorkOrderException, { isLoading: isFieldsLoading }] = useUpdateWorkOrderExceptionMutation()
  const [createException, { isLoading: isCreateLoading }] = useCreateWorkOrderExceptionMutation()
  const [addExceptionMedia, { isLoading: isMediaLoading }] = useAddExceptionMediaMutation()
  const businessId = useSelector((state: Record<string, unknown>) => get(state, 'AuthReducer.userInfo.businessInfo.businessId', ''))

  const watchedType = watch(EXCEPTION_FORM_FIELD.EXCEPTION_TYPE)
  const watchedLatitude = watch(EXCEPTION_FORM_FIELD.LATITUDE)
  const watchedLongitude = watch(EXCEPTION_FORM_FIELD.LONGITUDE)
  const isLocation = watchedType === EXCEPTION_TYPE.LOCATION

  const hasCoordinates = Boolean(watchedLatitude) && Boolean(watchedLongitude)
  const coordinatesLabel = `${watchedLatitude}, ${watchedLongitude}`

  const handleCopyCoordinates = useCallback(() => {
    navigator.clipboard
      .writeText(coordinatesLabel)
      .then(() => toast.success(T.COPIED_TO_CLIPBOARD))
      .catch(handleError)
  }, [coordinatesLabel])

  const handleExpandMap = useCallback(() => setIsMapExpanded(true), [])
  const handleCollapseMap = useCallback(() => setIsMapExpanded(false), [])

  // Guard against losing a filled-in form (incl. staged media) on an accidental dismiss.
  const isFormDirty = isDirty || media.length > 0

  const handleRequestClose = useCallback(() => {
    if (isSubmittingRef.current) return
    if (isFormDirty) setConfirmDiscardOpen(true)
    else onClose()
  }, [isFormDirty, onClose])

  const handleConfirmDiscard = useCallback(() => {
    setConfirmDiscardOpen(false)
    onClose()
  }, [onClose])

  const handleCancelDiscard = useCallback(() => setConfirmDiscardOpen(false), [])

  // Reset ONLY on the closed→open transition. `exception`/`note` get fresh identities on every
  // WO-details refetch (any CACHE_TAG_WORK_ORDER_DETAILS invalidation — another card's delete,
  // a background refetch), and resetting on those would wipe the user's in-flight edits and
  // staged media while the drawer is open.
  const wasOpenRef = useRef(false)
  useEffect(() => {
    if (open && !wasOpenRef.current) {
      reset(buildDefaults(exception, note, isCreate, initialContainerId))
      setMedia([])
      setConfirmDiscardOpen(false)
      setIsMapExpanded(false)
      isSubmittingRef.current = false
      setIsSubmitting(false)
    }
    wasOpenRef.current = open
  }, [open, exception, isCreate, note, initialContainerId, reset])

  // Client-direct media flow: bytes go straight to the private uploads bucket under the
  // tenant path (the exception id keys the folder, so uploads run AFTER create), then the
  // refs register via POST /{id}/media. A media failure is surfaced but never rolls back
  // the already-saved exception.
  const uploadStagedMedia = async (exceptionId: string) => {
    if (!media.length) return
    // Staged media with no upload context must fail loudly — a silent return here
    // reads as "saved without my photos" to the user.
    if (!exceptionId || !businessId || !accountId || !workOrderId) {
      throw new Error('Exception media upload is missing its tenant/account/work-order context')
    }
    const refs = await Promise.all(
      media.map(async file => ({
        uri: await uploadExceptionMediaFile({ businessId, accountId, workOrderId, exceptionId, file }),
        contentType: file.type || null,
        mediaType: file.type?.startsWith('video') ? 'VIDEO' : 'PHOTO',
      }))
    )
    await addExceptionMedia({ id: exceptionId, media: refs }).unwrap()
  }

  const submitException = async (data: ExceptionFormValues): Promise<boolean> => {
    // Geo is both-or-nothing on the BE (a lone/empty coordinate must be omitted, not
    // sent as '') — the point is only rewritten when both parse to numbers.
    const parsedLatitude = parseFloat(data.latitude)
    const parsedLongitude = parseFloat(data.longitude)
    const geoPayload =
      Number.isFinite(parsedLatitude) && Number.isFinite(parsedLongitude) ? { latitude: parsedLatitude, longitude: parsedLongitude } : {}
    const containerId =
      data.workOrderContainerId && data.workOrderContainerId !== EXCEPTION_TARGET_WORK_ORDER ? data.workOrderContainerId : null
    // Delay has no field of its own on the create/update DTOs — the elapsed ms persists through
    // `description` (the per-type value channel; getExceptionDelayMs reads it back for the card).
    const parsedDelayMs = parseInt(data.delayMs, 10)
    const delayDescription = parsedDelayMs > 0 ? String(parsedDelayMs) : ''
    // Non-channel types leave `description` free, so mirror the note there for the mobile app;
    // fall back to the existing description so a legacy note (empty noteText) isn't cleared.
    const structuredDescription = data.exceptionType === EXCEPTION_TYPE.DELAY ? delayDescription : data.description
    const description = usesDescriptionChannel(data.exceptionType) ? structuredDescription : data.noteText || data.description

    if (isCreate) {
      let createdId = ''
      try {
        const created = await createException({
          workOrderId,
          exceptionType: data.exceptionType,
          noteText: data.noteText,
          description,
          responsibleParty: data.responsibleParty,
          containerId,
          ...geoPayload,
        }).unwrap()
        createdId = String(get(created, 'id', '') || '')
      } catch (error) {
        handleError(error)
        return false
      }
      try {
        await uploadStagedMedia(createdId)
        toast.success(T.EXCEPTION_CREATED_SUCCESS)
      } catch (error) {
        handleError(error)
        toast.warning(T.EXCEPTION_MEDIA_UPLOAD_FAILED)
      }
      onClose()
      return true
    }

    // PATCH is full-replace: every editable field must ride along or the BE clears it — containerId
    // included, or the save would unlink the container. responsibleParty is the exception: the
    // work-order list payload omits it, so this drawer cannot always show the stored value, and
    // sending an untouched field would erase it. The BE leaves the column alone when it is absent.
    try {
      await updateWorkOrderException({
        id: id as string,
        exceptionType: data.exceptionType,
        noteText: data.noteText,
        description,
        responsibleParty: dirtyFields.responsibleParty ? data.responsibleParty : undefined,
        containerId,
        ...geoPayload,
      }).unwrap()
    } catch (error) {
      handleError(error)
      return false
    }
    try {
      await uploadStagedMedia(id as string)
    } catch (error) {
      handleError(error)
      toast.warning(T.EXCEPTION_MEDIA_UPLOAD_FAILED)
    }
    toast.success(T.EXCEPTION_UPDATED_SUCCESS)
    onClose()
    return true
  }

  // On success the lock is NEVER released here — re-enabling before the drawer finished closing left a
  // click window that double-created. It resets on the next closed→open transition; errors unlock for retry.
  const onSubmit = async (data: ExceptionFormValues) => {
    if (isSubmittingRef.current) return
    isSubmittingRef.current = true
    setIsSubmitting(true)
    let succeeded = false
    try {
      succeeded = await submitException(data)
    } finally {
      if (!succeeded) {
        isSubmittingRef.current = false
        setIsSubmitting(false)
      }
    }
  }

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        PaperProps={{
          sx: {
            display: 'flex',
            flexDirection: 'column',
            height: `calc(100% - ${MAIN_HEADER_HEIGHT}px)`,
            top: MAIN_HEADER_HEIGHT,
            width: DRAWER_WIDTH,
          },
        }}
        onClose={handleRequestClose}
      >
        <HHDialogTitle
          // Close is absolute against the drawer paper, so centring it in the taller action row is a fixed top.
          closeIconSx={headerActions ? { top: 16 } : undefined}
          // The title stack is inline-flex, so it only reaches the close button once it spans the row.
          stackSx={headerActions ? { width: '100%' } : undefined}
          sx={{ borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}
          title={isCreate ? T.NEW_EXCEPTION : T.EXCEPTION_DETAIL}
          onClose={handleRequestClose}
        >
          {headerActions}
        </HHDialogTitle>
        {isSubmitting && <LinearProgress />}

        <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
          {/* No-extra-clicks invoiced warning (per product): the create flow proceeds
              normally, this alert plus the "+ Exception" tooltip carry the caution. */}
          {isCreate && isWorkOrderInvoiced && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {T.WO_INVOICED_EXCEPTION_WARNING}
            </Alert>
          )}
          {/* Target leads the whole drawer: whether the exception is for the WO or one
              container is the first decision, before media and details. */}
          {isCreate && (
            <Box mb={2}>
              <ExceptionTargetSelect containers={containers} control={control} />
            </Box>
          )}

          {/* Set location shows the location map; every other type shows the photo gallery
              (upload "+" pending the web image-upload endpoint — BE gap). */}
          {isLocation ? (
            <ExceptionMapImage
              height={240}
              id={`exception-detail-map-${id || 'new'}`}
              latitude={Number(watchedLatitude) || undefined}
              longitude={Number(watchedLongitude) || undefined}
            />
          ) : (
            <Box sx={onMediaClick ? { cursor: 'pointer' } : undefined} onClick={onMediaClick}>
              <ExceptionCardMediaSwiperContainerUntyped images={images}>
                {!hasImages && <ImagePlaceholderUntyped sx={{ height: 240 }} />}
                {hasImages &&
                  images.map(image =>
                    get(image, 'kind') === EXCEPTION_MEDIA_KIND.VIDEO ? (
                      <ExceptionVideoSlide key={getExceptionMediaSlideKey(image)} height={240} thumbnailUri={image.thumbnailUri} />
                    ) : (
                      <ExceptionImageUntyped
                        key={getExceptionMediaSlideKey(image)}
                        withSwiperSlide
                        accountId={accountId}
                        exception={exception}
                        photo={image}
                        size="medium"
                        workOrderId={workOrderId}
                      />
                    )
                  )}
                <ExceptionCardBottomOverlayUntyped date={date} time={time} />
              </ExceptionCardMediaSwiperContainerUntyped>
            </Box>
          )}

          {hasCoordinates && (
            <Stack alignItems="center" direction="row" flexWrap="wrap" gap={0.5} mt={1}>
              <Typography color="text.secondary" variant="body2">
                {coordinatesLabel}
              </Typography>
              <Tooltip title={T.COPY_COORDINATES}>
                <IconButton aria-label={T.COPY_COORDINATES} size="small" onClick={handleCopyCoordinates}>
                  <ContentCopyIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
              {/* Labelled rather than icon-only: the pop-out is the whole point of the row for
                  dispatch, and an unlabelled icon read as decoration. No tooltip — it would
                  only repeat the visible text. */}
              <Button
                size="small"
                startIcon={<OpenInFullIcon sx={{ fontSize: 16 }} />}
                sx={{ minWidth: 0, px: 0.75 }}
                onClick={handleExpandMap}
              >
                {T.EXPAND_LOCATION}
              </Button>
            </Stack>
          )}

          <Stack gap={2} mt={2}>
            {!isLocation && <ExceptionMediaUploader files={media} onChange={setMedia} />}
            <HHFormTextField
              fullWidth
              multiline
              control={control}
              deprecatedLabel={false}
              label={T.NOTES}
              maxRows={6}
              minRows={2}
              name={EXCEPTION_FORM_FIELD.NOTE_TEXT}
            />
            <ExceptionTypeSelect control={control} />
            <ExceptionTypeFields control={control} exceptionType={watchedType} />
          </Stack>

          {!isCreate && <ExceptionAuditTrail revisions={revisions} />}
        </Box>

        <Stack
          alignItems="center"
          direction="row"
          justifyContent="space-between"
          sx={{ borderTop: 1, borderColor: 'divider', flexShrink: 0, p: 2 }}
        >
          <CancelButton disabled={isSubmitting} onClick={handleRequestClose} />
          <Button
            loading={isSubmitting || isFieldsLoading || isCreateLoading || isMediaLoading}
            size="small"
            variant="contained"
            onClick={handleSubmit(onSubmit)}
          >
            {T.SAVE}
          </Button>
        </Stack>
      </Drawer>

      <Dialog fullWidth maxWidth="lg" open={isMapExpanded} onClose={handleCollapseMap}>
        <DialogTitle sx={{ p: 2 }}>
          <Stack alignItems="center" direction="row" justifyContent="space-between">
            <Typography fontWeight={600} variant="h5">
              {T.LOCATION}
            </Typography>
            <IconButton aria-label={T.CLOSE} size="small" onClick={handleCollapseMap}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {isMapExpanded && (
            <ExceptionMapImage
              enableTypeToggle
              gestureHandling="greedy"
              height={EXPANDED_MAP_HEIGHT}
              id={`exception-detail-map-expanded-${id || 'new'}`}
              latitude={Number(watchedLatitude) || undefined}
              longitude={Number(watchedLongitude) || undefined}
            />
          )}
        </DialogContent>
      </Dialog>

      <HHConfirmDialog
        ConfirmButtonProps={{ color: 'error' }}
        confirmButtonTitle={T.DISCARD_CHANGES}
        confirmDescription={T.DISCARD_UNSAVED_CHANGES_MSG}
        confirmTitle={T.UNSAVED_CHANGES}
        isOpen={confirmDiscardOpen}
        onClose={handleCancelDiscard}
        onConfirm={handleConfirmDiscard}
      />
    </>
  )
}

export default ExceptionDetailModal
